import { Button, FormControl, FormControlLabel, FormHelperText, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import React from 'react';
import { useInboxTranslation } from '../../Hooks';
import { NotificationItem } from '../../Models';

interface Props {
    notificationItem: NotificationItem;
    onSubmitResponse: (notificationId: string, response: string) => void;
}

export const Response: React.FC<Props> = (props) => {
    const { t } = useInboxTranslation();
    const [selectedOption, setSelectedOption] = React.useState('');
    const [customResponse, setCustomResponse] = React.useState('');
    const [error, setError] = React.useState<string>();
    const [customValueError, setCustomValueError] = React.useState<string>();
    const responseOptionsAvailable =
        (props.notificationItem.responseOptions && Object.keys(props.notificationItem.responseOptions).length > 0) ||
        props.notificationItem.responseIsFreeText;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedOption((event.target as HTMLInputElement).value);
        setError(undefined);
    };
    const handleSubmit = () => {
        if (selectedOption === '') {
            setError(t('inboxDetails.valueRequired'));
            return;
        }
        if (selectedOption === 'custom' && (!customResponse || customResponse.trim().length === 0)) {
            setCustomValueError(t('inboxDetails.valueRequired'));
            return;
        }
        const response = selectedOption && selectedOption === 'custom' ? customResponse.trim() : selectedOption;
        props.onSubmitResponse(props.notificationItem.id, response);
    };
    return (
        <>
            <FormControl component='fieldset' sx={{ display: 'block', pb: 1 }}>
                {!responseOptionsAvailable && (
                    <Typography data-test='no-response-options'>{t('inboxDetails.noResponseOptions')}</Typography>
                )}
                {responseOptionsAvailable && props.notificationItem.response && !props.notificationItem.waitingForResponse && (
                    <Typography sx={{ wordBreak: 'break-word' }} data-test='you-responded'>
                        {t('inboxDetails.youResponded', { value: getResponseText(props.notificationItem) })}
                    </Typography>
                )}
                {responseOptionsAvailable && !props.notificationItem.response && !props.notificationItem.waitingForResponse && (
                    <Typography data-test='message-is-no-longer-available-for-response'>
                        {t('inboxDetails.messageIsNoLongerAvailableForResponse')}
                    </Typography>
                )}
                {responseOptionsAvailable && props.notificationItem.waitingForResponse && (
                    <>
                        {error && <FormHelperText error={true}>{error}</FormHelperText>}
                        <RadioGroup value={selectedOption} onChange={handleChange} data-test='select-response-option'>
                            {props.notificationItem?.responseOptions &&
                                Object.entries(props.notificationItem.responseOptions).map(([key, value]) => (
                                    <FormControlLabel
                                        key={key}
                                        value={key}
                                        control={<Radio color='primary' />}
                                        label={<Typography sx={{ wordBreak: 'break-word' }}>{value}</Typography>}
                                        disabled={!props.notificationItem.waitingForResponse}
                                    />
                                ))}
                            {props.notificationItem.responseIsFreeText && (
                                <FormControlLabel
                                    key='custom'
                                    value='custom'
                                    control={<Radio color='primary' />}
                                    label={t('inboxDetails.custom') as string}
                                    disabled={!props.notificationItem.waitingForResponse}
                                />
                            )}
                        </RadioGroup>
                        {selectedOption === 'custom' && (
                            <TextField
                                value={customResponse}
                                variant='outlined'
                                size='small'
                                label={t('inboxDetails.customResponseValue')}
                                onChange={(event) => setCustomResponse(event.target.value)}
                                disabled={!props.notificationItem.waitingForResponse}
                                fullWidth={true}
                                error={!!customValueError}
                                helperText={customValueError}
                                data-test='custom-response'
                            />
                        )}
                    </>
                )}
            </FormControl>
            {responseOptionsAvailable && props.notificationItem.waitingForResponse && (
                <Button variant='contained' color='primary' onClick={handleSubmit}>
                    {t('inboxDetails.submit')}
                </Button>
            )}
        </>
    );
};

function getResponseText(notificationItem: NotificationItem) {
    const option = Object.entries(notificationItem?.responseOptions ?? {}).find((opt) => opt[0] === notificationItem.response);
    return option ? option[1] : notificationItem.response;
}
